<template>
  <div
    class="chat-composer relative w-full"
    :class="{
      'chat-composer--rewrite': rewriteActive,
      'chat-composer--rewrite-streaming': rewriteStreaming,
    }"
  >
    <ComposerFrame
      :layout-key="typographyKey"
      :model-value="normalizedState.message"
      variant="inline"
      :placeholder="messagePlaceholder"
      :expanded="showTools && menuOpen"
      :is-streaming="isStreaming"
      :can-send="canSend && !disabled"
      :disabled="disabled"
      :readonly="inputReadonly"
      idle-translucent
      :filled="isFilled"
      @update:model-value="patchState({ message: $event })"
      @update:expanded="menuOpen = $event"
      @send="$emit('send')"
      @stop="$emit('stop')"
    >
      <!-- Leading: the chevron opens the director-note menu. A primary dot rides
           the corner whenever a note is attached to the message being drafted. -->
      <template v-if="showTools" #leading>
        <BaseButton
          type="button"
          variant="icon"
          size="icon"
          :color="menuOpen ? 'primary' : ''"
          :disabled="disabled"
          aria-label="Composer menu"
          @click="toggleMenu"
        >
          <template #icon>
            <span class="director-chevron-wrap">
              <span class="director-chevron" :class="{ 'director-chevron--open': menuOpen }">
                <ChevronUpIcon :size="20" aria-hidden="true" />
              </span>
              <span v-if="(hasNote || rewriteActive) && !menuOpen" class="director-chevron__badge" aria-hidden="true" />
            </span>
          </template>
        </BaseButton>
      </template>

      <template v-if="showTools" #expansion>
        <DirectorNoteMenu
          :note="normalizedState.note"
          :director-notes="directorNotes"
          :placement="normalizedState.placement"
          :expires="normalizedState.expires"
          :life="normalizedState.life"
          :disabled="disabled"
          :note-placeholder="notePlaceholder"
          :can-rewrite="canRewrite"
          :rewrite-active="rewriteActive"
          :rewrite-streaming="rewriteStreaming"
          :rewrite-bot-name="rewriteBotName"
          @update:note="patchState({ note: $event })"
          @update:placement="patchState({ placement: $event })"
          @update:expires="patchState({ expires: $event })"
          @update:life="patchState({ life: $event })"
          @rewrite="requestRewrite"
          @restore-original="restoreOriginal"
        />
      </template>
    </ComposerFrame>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import ChevronUpIcon from 'vue-material-design-icons/ChevronUp.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import ComposerFrame from '@/components/composer/ComposerFrame.vue'
import DirectorNoteMenu from '@/features/story-conversation/components/DirectorNoteMenu.vue'
import { useConversationTypography } from '@/components/chat/useConversationTypography'
import type { ChatComposerState } from '@/components/chat/composerState'
import type { DirectorNotePlacement } from '@/types/domain'

const props = withDefaults(defineProps<{
  state: ChatComposerState
  canSend?: boolean
  isStreaming?: boolean
  disabled?: boolean
  inputReadonly?: boolean
  messagePlaceholder?: string
  notePlaceholder?: string
  showTools?: boolean
  directorNotes?: boolean
  canRewrite?: boolean
  rewriteMode?: 'idle' | 'streaming' | 'review'
  rewriteBotName?: string
}>(), {
  canSend: false,
  isStreaming: false,
  disabled: false,
  inputReadonly: false,
  messagePlaceholder: 'Message...',
  notePlaceholder: 'Write your note...',
  showTools: true,
  directorNotes: true,
  canRewrite: false,
  rewriteMode: 'idle',
  rewriteBotName: '',
})

const emit = defineEmits<{
  (event: 'update:state', value: ChatComposerState): void
  (event: 'send'): void
  (event: 'stop'): void
  (event: 'rewrite'): void
  (event: 'restore-original'): void
}>()

const menuOpen = ref(false)
const typographyKey = useConversationTypography()

const normalizedState = computed(() => ({
  message: props.state?.message ?? '',
  note: props.state?.note ?? '',
  placement: (props.state?.placement === 'prepend' ? 'prepend' : 'append') as DirectorNotePlacement,
  expires: Boolean(props.state?.expires),
  life: Number.isFinite(Number(props.state?.life)) && Number(props.state.life) > 0 ? Math.floor(Number(props.state.life)) : 3,
}))

const hasNote = computed(() => props.showTools && normalizedState.value.note.trim() !== '')
const isFilled = computed(() => normalizedState.value.message.trim() !== '' || hasNote.value)
const rewriteActive = computed(() => props.rewriteMode !== 'idle')
const rewriteStreaming = computed(() => props.rewriteMode === 'streaming')

function patchState(partial: Partial<ChatComposerState>) {
  emit('update:state', {
    ...props.state,
    ...partial,
  })
}

function toggleMenu() {
  if (props.disabled) return
  menuOpen.value = !menuOpen.value
}

function requestRewrite() {
  menuOpen.value = false
  emit('rewrite')
}

function restoreOriginal() {
  menuOpen.value = false
  emit('restore-original')
}
</script>

<style scoped>
/* Match the chat's prose voice in the inline textarea (the frame exposes these
   as CSS custom properties so consumers set the type without overriding rules). */
.chat-composer :deep(.composer-frame) {
  --composer-input-color: var(--theme-on-surface, #fff);
  --composer-input-font: 400 1.125rem/1.7 var(--story-font-family, Georgia, serif);
  --composer-input-placeholder: color-mix(in srgb, var(--theme-on-surface-variant, rgba(255, 255, 255, 0.5)) 50%, transparent);
}

.chat-composer--rewrite :deep(.composer-frame) {
  --composer-border: color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 42%, var(--theme-border, rgba(255, 255, 255, 0.1)));
  --composer-shadow: 0 8px 24px color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 10%, transparent);
}

.director-chevron-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.director-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 150ms ease;
}

.director-chevron--open {
  transform: rotate(180deg);
}

.director-chevron__badge {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--theme-primary, rgb(29 138 255));
}
</style>
